import { useMutation, useQueryClient } from '@northladder/services';
import {
  ErrorEventsEnum,
  errorLogToRemoteUtil,
  IApiError,
} from '@northladder/utilities';

import {
  CATEGORY_FILTER_KEY,
  FETCH_ALL_BIDS_KEY,
  FETCH_ALL_BIDS_PAAS_KEY,
  FETCH_UPLOAD_BID_FILES_KEY,
  MODELS_FILTER_KEY,
} from '../../bids';
import { FETCH_APPRAISALS_KEY } from '../../customer-service';

import { ILoginUserData } from '../types';
import { USER_ACCOUNTS_API } from '../userAccountsAPIClient';

/**
 * This is to track the login mutation keys in react query cache.
 */
const MUT_LOGIN_USER = 'login-mutation-key';

interface IUseLoginOptions {
  /**
   * Callback to be by the calling app if the login is successful.
   * Note: Usually this would be best handled within the onSuccess of the hook
   * itself but since the login would require additional platform specific
   * features like `next/navigation/router` & `localStorage` on the web and
   * `react-navigation` & `asyncStorage` on mobile, this will be done in the
   * callback of the respective platform without having to embed them in the util.
   */
  onSuccessCallback: (loginData: ILoginUserData) => void;
  onErrorCallback?: (err: IApiError) => void;
}

/**
 * This performs a login request and retrieves the authed user info and token.
 * Provides data fetching states and a callback to be used in the redirection later.
 */
export const useLoginMutation = ({
  onSuccessCallback,
  onErrorCallback,
}: IUseLoginOptions) => {
  const queryClient = useQueryClient();
  const { mutate, isPending, error, data } = useMutation({
    mutationKey: [MUT_LOGIN_USER],
    networkMode: 'always', // Even make calls when offline
    retry: false, // For login Request, do not retry failed requests.
    mutationFn: USER_ACCOUNTS_API.loginUser,
    onSuccess: (loginData) => {
      queryClient.invalidateQueries({
        queryKey: [FETCH_UPLOAD_BID_FILES_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [FETCH_ALL_BIDS_PAAS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [FETCH_APPRAISALS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CATEGORY_FILTER_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [FETCH_ALL_BIDS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [MODELS_FILTER_KEY],
      });
      onSuccessCallback(loginData as ILoginUserData);
    },
    onError: (err: IApiError) => {
      errorLogToRemoteUtil({
        error,
        errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
        errorTitle: 'Error in Session renewal Attempt',
        message: error?.message,
      });

      onErrorCallback?.(err);

      return err;
    },
  });

  return {
    data,
    error,
    isPending,
    submitLogin: mutate,
  };
};
